import './DonationSummary.scss';
import React, { FunctionComponent } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import parseCurrency from '../../utils/parseCurrency';
import PageSection from '../PageSection/PageSection';
import DonationSummaryItem from './DonationSummaryItem';
import classNames from 'classnames';
import clientConfig from '../../client/config';
import useBasePageQuery from '../../hooks/useBasePageQuery';

export enum DonationField {
  OnlineAmount = 'onlineAmount',
  OfflineAmount = 'offlineAmount',
  DirectDonationAmount = 'directDonationAmount',
  FundraiserRaisedAmount = 'fundraiserRaisedAmount',
  TextDonations = 'textDonations',
}

type DonationSummaryProps = {
  fieldsToShow?: DonationField[];
};

export const DonationSummary: FunctionComponent<DonationSummaryProps> = props => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { data } = useBasePageQuery();
  const { fieldsToShow } = props;

  if (!data?.page?.donationSummary) {
    return null;
  }

  const { donationSummary } = data.page;

  const getDonationSummaryTranslation = (key: string) => {
    switch (key) {
      case 'onlineAmount': {
        return t('donations.onlineAmount');
      }
      case 'offlineAmount': {
        return t('donations.offlineAmount');
      }
      case 'directDonationAmount': {
        return t('donations.directDonationAmount');
      }
      case 'fundraiserRaisedAmount': {
        return t('donations.fundraiserRaisedAmount');
      }
      case 'textDonations': {
        return t('donations.textDonations');
      }
      default: {
        return '';
      }
    }
  };

  const summaryItems = fieldsToShow
    ?.map(field => {
      if (donationSummary) {
        const item = donationSummary[field];
        if (item) {
          return {
            amount: item?.value,
            currencyCode: item?.currencyCode,
            label: getDonationSummaryTranslation(field),
          };
        }
        return null;
      }
      return null;
    })
    .filter(x => x !== null);

  const { totalAmount, totalMatched } = donationSummary;

  let totalGiftAid;
  if (totalMatched.length > 0) {
    const matchedGiftAid = totalMatched.filter(
      matchedItem => matchedItem.type === 'GIFT_AID',
    );
    totalGiftAid = matchedGiftAid[0].amount.value;
  }

  return (
    <PageSection
      title={t('donations.title', 'Donation summary')}
      fullWidth
      titleClass={classNames('jg-h5', 'jg-space-mbmd')}
    >
      <dl
        className="jg-pages-donationsummary__list"
        aria-label="donationSummary"
        aria-describedby="donationsInfo"
      >
        <div>
          <dt
            className={classNames(
              'jg-pages-donationsummary__totaltitle',
              'jg-text--small',
              'jg-space-mbsm',
            )}
          >
            {t('donations.totalRaised', 'Total raised')}
          </dt>
          <dd
            className={classNames(
              'jg-pages-donationsummary__total jg-space-mbsm',
              'jg-text-color-branded',
            )}
            data-qa="totalValue"
          >
            {totalAmount &&
              parseCurrency(totalAmount.currencyCode, totalAmount.value / 100)}
            {totalAmount &&
              typeof totalGiftAid === 'number' &&
              totalGiftAid > 0 && (
                <small
                  className="jg-pages-donationsummary__giftaid"
                  aria-label="giftAid"
                >
                  {' + '}
                  {parseCurrency(
                    totalAmount.currencyCode,
                    totalGiftAid / 100,
                  )}{' '}
                  {t('common.giftaid', 'Gift Aid')}
                </small>
              )}
          </dd>
        </div>

        {summaryItems &&
          summaryItems.map(item => {
            if (item) {
              return (
                <DonationSummaryItem key={item?.label} label={item.label}>
                  {parseCurrency(item?.currencyCode, item?.amount / 100)}
                </DonationSummaryItem>
              );
            }
            return null;
          })}
      </dl>
      <p className="jg-text--small-light jg-space-mbn" id="donationsInfo">
        <Trans i18nKey="donations.info">
          * Charities pay a small fee for our service.{' '}
          <a
            href={`${clientConfig.baseUri}/fees`}
            rel="noopener noreferrer"
            target="_blank"
          >
            Find out how much it is and what we do for it.
          </a>
        </Trans>
      </p>
    </PageSection>
  );
};

export default DonationSummary;
